import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { isQAOverrideUser } from "@/lib/qaOverride";

export interface DailyPaycheck {
  id: string;
  user_id: string;
  pay_date: string;
  career_key_snapshot: string;
  comp_type_snapshot: string;
  gross_pay: number;
  federal_tax: number;
  state_tax: number;
  social_security: number;
  medicare: number;
  benefits: number;
  retirement: number;
  net_pay: number;
  allocation: Record<string, number>;
  status: "pending_allocation" | "allocated";
  allocated_at: string | null;
  created_at: string;
}

/**
 * Daily paycheck client. claim() is idempotent server-side
 * (UNIQUE on user_id + pay_date). QA admins are short-circuited.
 */
export function useDailyPaycheck() {
  const { user } = useAuth();
  const [history, setHistory] = useState<DailyPaycheck[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("daily_paychecks")
      .select("*")
      .eq("user_id", user.id)
      .order("pay_date", { ascending: false })
      .limit(60);
    setHistory(((data) || []) as DailyPaycheck[]);
  }, [user]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const claim = useCallback(async (): Promise<DailyPaycheck | null> => {
    if (!user) return null;
    if (await isQAOverrideUser(user.id)) return null;
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("claim_daily_paycheck");
      if (error) return null;
      const row = Array.isArray(data) ? data[0] : data;
      if (row) await fetchHistory();
      return (row as DailyPaycheck) || null;
    } finally {
      setLoading(false);
    }
  }, [user, fetchHistory]);

  const allocate = useCallback(
    async (paycheckId: string, allocation: Record<string, number>) => {
      const { data, error } = await supabase.rpc("allocate_paycheck", {
        p_paycheck_id: paycheckId,
        p_allocation: allocation,
      });
      if (error) throw new Error(error.message);
      await fetchHistory();
      return data as { ok: boolean; reason?: string };
    },
    [fetchHistory]
  );

  return { history, loading, claim, allocate, refetch: fetchHistory };
}
